import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpHeaders
} from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, finalize, shareReplay, switchMap } from 'rxjs/operators';
import { AuthenticationService } from '@app/services/authentication.service';
import { Router } from "@angular/router";
import {BsModalService} from "ngx-bootstrap/modal";
import { NotificationService } from '@app/services/notification.service';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private refreshRequest$: Observable<any> | null = null;
  private readonly alreadyRotatedRetryDelayMs = 250;
  private readonly maxAlreadyRotatedRetries = 2;
  private readonly temporaryRefreshFailureMessage = 'Session refresh is temporarily unavailable. Please try again.';

  constructor(private router: Router,
    private authenticationService: AuthenticationService,
              private modalService: BsModalService,
              private notificationService: NotificationService
  ) { }

  /**
   * Add header info to all new HttpClient calls
   * @param request
   * @param next
   */
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const credentialRequest = request.clone({ withCredentials: true });

    return next.handle(credentialRequest).pipe(
      catchError((err: HttpErrorResponse) => {
        if (!this.shouldTryRefresh(err, request)) {
          return throwError(() => err);
        }

        return this.refreshOnce().pipe(
          switchMap(() => next.handle(request.clone({ withCredentials: true }))),
          catchError((refreshErr: HttpErrorResponse) => {
            if (this.isAlreadyRotatedRefresh(refreshErr)) {
              return this.retryOriginalAfterAlreadyRotated(request, next, 0);
            }

            if (this.isTemporaryRefreshFailure(refreshErr)) {
              return this.handleTemporaryRefreshFailure(refreshErr);
            }

            this.redirectToLogin();
            return throwError(() => refreshErr);
          })
        );
      })
    );
  }

  private refreshOnce(): Observable<any> {
    if (!this.refreshRequest$) {
      this.refreshRequest$ = this.authenticationService.refresh().pipe(
        shareReplay(1),
        finalize(() => {
          this.refreshRequest$ = null;
        })
      );
    }

    return this.refreshRequest$;
  }

  private shouldTryRefresh(err: HttpErrorResponse, request: HttpRequest<any>): boolean {
    return err.status === 401 && !this.isAuthenticationEndpoint(request.url);
  }

  private isAuthenticationEndpoint(url: string): boolean {
    const normalizedUrl = url.split('?')[0].replace(/\/+$/, '');
    return /(^|\/)(auth|customer\/auth)(\/|$)/.test(normalizedUrl);
  }

  private isAlreadyRotatedRefresh(err: HttpErrorResponse): boolean {
    return err.status === 409 && err.headers?.get('X-Refresh-Token-Result') === 'ALREADY_ROTATED';
  }

  private isTemporaryRefreshFailure(err: HttpErrorResponse): boolean {
    return err.status === 503 && ['REDIS_UNAVAILABLE', 'REDIS_UNCERTAIN'].includes(this.refreshTokenResult(err));
  }

  private refreshTokenResult(err: HttpErrorResponse): string {
    return err.headers?.get('X-Refresh-Token-Result') || '';
  }

  private handleTemporaryRefreshFailure(err: HttpErrorResponse): Observable<never> {
    this.notificationService.showErrorMessage(this.temporaryRefreshFailureMessage);
    return throwError(() => this.temporaryRefreshError(err));
  }

  private temporaryRefreshError(err: HttpErrorResponse): HttpErrorResponse {
    return new HttpErrorResponse({
      error: {
        temporaryRefreshFailure: true,
        resultCode: this.refreshTokenResult(err),
        message: this.temporaryRefreshFailureMessage
      },
      headers: err.headers || new HttpHeaders(),
      status: err.status,
      statusText: err.statusText || 'Service Unavailable',
      url: err.url || undefined
    });
  }

  private retryOriginalAfterAlreadyRotated(
    request: HttpRequest<any>,
    next: HttpHandler,
    retryCount: number
  ): Observable<HttpEvent<any>> {
    return timer(this.alreadyRotatedRetryDelayMs).pipe(
      switchMap(() => next.handle(request.clone({ withCredentials: true }))),
      catchError((retryErr: HttpErrorResponse) => {
        if (retryErr.status === 401 && retryCount + 1 < this.maxAlreadyRotatedRetries) {
          return this.retryOriginalAfterAlreadyRotated(request, next, retryCount + 1);
        }

        this.redirectToLogin();
        return throwError(() => retryErr);
      })
    );
  }

  private redirectToLogin(): void {
    this.modalService.hide();
    this.authenticationService.logout();
    this.router.navigate(['/login']);
  }

}
